import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { HABIT_TEMPLATES } from '../lib/templates';
import { Button } from '../components/ui/button';
import * as LucideIcons from 'lucide-react';
import { toast } from 'sonner';

const toIconKey = (n) => (n || 'sparkles').split('-').map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join('');

export default function Templates() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [adding, setAdding] = useState(null);

  const add = async (tpl) => {
    setAdding(tpl.name);
    const { error } = await supabase.from('habits').insert({
      user_id: user.id,
      name: tpl.name,
      description: tpl.description,
      type: tpl.type,
      target_value: tpl.target_value,
      unit: tpl.unit,
      color: tpl.color,
      icon: tpl.icon,
      time_of_day: tpl.time_of_day,
      schedule_type: tpl.schedule_type,
      schedule_days: tpl.schedule_days || [],
    });
    setAdding(null);
    if (error) { toast.error(error.message); return; }
    toast.success(`${tpl.name} added`);
    navigate('/today');
  };

  return (
    <div className="space-y-6" data-testid="templates-page">
      <div>
        <p className="uppercase tracking-[0.2em] text-xs text-muted-foreground font-medium">Inspiration</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mt-1">Habit templates</h1>
        <p className="text-sm text-muted-foreground mt-2">A curated start — tap to add. Tweak anytime.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {HABIT_TEMPLATES.map((tpl) => {
          const I = LucideIcons[toIconKey(tpl.icon)] || LucideIcons.Sparkles;
          return (
            <div key={tpl.name} className="rounded-2xl border border-border bg-card/30 p-5 hover:-translate-y-1 transition-transform" data-testid={`template-card-${tpl.name}`}>
              <div className="h-11 w-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${tpl.color}1F`, color: tpl.color }}>
                <I className="h-5 w-5" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading font-medium mt-4">{tpl.name}</h3>
              <p className="text-xs text-muted-foreground mt-1 min-h-[2.5rem]">{tpl.description}</p>
              <div className="flex justify-between items-center mt-4">
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
                  {tpl.type === 'binary' ? 'Check' : tpl.type === 'time' ? `${tpl.target_value} min` : `${tpl.target_value} ${tpl.unit}`}
                </span>
                <Button size="sm" className="rounded-full" disabled={adding === tpl.name} onClick={() => add(tpl)} data-testid={`template-add-${tpl.name}`}>
                  {adding === tpl.name ? 'Adding...' : 'Add'}
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
